import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Box,
	Button,
	Container,
	Typography,
	Card,
	Grid,
	Chip,
	Collapse,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	AppBar,
	Toolbar,
	CircularProgress,
	Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled, keyframes } from "@mui/material/styles";
import { motion } from "framer-motion";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StripeService from "../../services/stripe/StripeService";
import { useSubscription } from "../../contexts/SubscriptionContext";
import {
	TierId,
	TIER_DISPLAY,
	PRICING_PAGE_TIERS,
	formatTierPrice,
} from "../../config/tierDisplay";
import { usePlanCatalog } from "../../hooks/usePlanCatalog";
import ContactSalesModal from "../../components/ContactSalesModal";

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const PageContainer = styled(Box)(() => ({
	position: "relative",
	minHeight: "100vh",
	background: "linear-gradient(-45deg, #f5f7fa, #e9ecef, #f8f9fa, #f1f3f4)",
	backgroundSize: "400% 400%",
	animation: `${gradientShift} 15s ease infinite`,
}));

const StyledAppBar = styled(AppBar)(() => ({
	background: "#ffffff",
	boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
	position: "sticky",
	top: 0,
	zIndex: 1000,
}));

const HeroSection = styled(Box)(() => ({
	padding: "40px 0 60px 0",
	textAlign: "center",
	position: "relative",
}));

const PricingCard = styled(Card, {
	shouldForwardProp: (prop) => prop !== "featured",
})<{ featured?: boolean }>(({ featured }) => ({
	position: "relative",
	padding: "32px",
	borderRadius: "16px",
	boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
	transition: "all 0.3s ease",
	border: featured ? "2px solid #5DBEC3" : "1px solid #dee2e6",
	height: "100%",
	display: "flex",
	flexDirection: "column",
	"&:hover": {
		transform: "translateY(-4px)",
		boxShadow: featured
			? "0 8px 32px rgba(93, 190, 195, 0.2)"
			: "0 8px 32px rgba(0, 0, 0, 0.12)",
	},
}));

const FeaturedBadge = styled(Chip)(() => ({
	position: "absolute",
	top: "16px",
	right: "16px",
	background: "linear-gradient(135deg, #5DBEC3 0%, #4BA3A7 100%)",
	color: "#ffffff",
	fontWeight: 600,
	fontSize: "12px",
	height: "24px",
}));

const PlanIcon = styled(Box)<{ variant: TierId }>(({ variant }) => ({
	width: "64px",
	height: "64px",
	borderRadius: "16px",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 24px auto",
	background:
		variant === "starter"
			? "#f8f9fa"
			: variant === "core"
				? "linear-gradient(135deg, #5DBEC3 0%, #4BA3A7 100%)"
				: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
	color: variant === "starter" ? "#6c757d" : "#ffffff",
}));

const CTAButton = styled(Button)<{ buttonvariant: TierId }>(
	({ buttonvariant }) => ({
		background:
			buttonvariant === "starter"
				? "#ffffff"
				: buttonvariant === "core"
					? "linear-gradient(135deg, #5DBEC3 0%, #4BA3A7 100%)"
					: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
		color: buttonvariant === "starter" ? "#2c3e50" : "#ffffff",
		border: buttonvariant === "starter" ? "1px solid #dee2e6" : "none",
		fontWeight: 600,
		fontSize: "16px",
		textTransform: "none",
		borderRadius: "8px",
		padding: "14px 32px",
		width: "100%",
		"&:hover": {
			transform: "translateY(-2px)",
		},
	}),
);

const FeatureItem = styled(ListItem)(() => ({
	padding: "4px 0",
	"& .MuiListItemIcon-root": {
		minWidth: "32px",
	},
}));

const ModernPricing: React.FC = () => {
	const navigate = useNavigate();
	const { getAccessTokenSilently, isAuthenticated } = useAuth0();
	const { state: subscriptionState, refreshSubscription } = useSubscription();
	const {
		planFor,
		loading: catalogLoading,
		error: catalogError,
	} = usePlanCatalog();
	const [loading, setLoading] = useState(false);
	const [loadingTier, setLoadingTier] = useState<TierId | null>(null);
	const [error, setError] = useState("");
	const [expandedExclusions, setExpandedExclusions] = useState<Set<TierId>>(
		new Set(),
	);
	const [contactSalesOpen, setContactSalesOpen] = useState(false);
	const stripeService = StripeService.getInstance();

	const toggleExclusions = (tier: TierId) => {
		setExpandedExclusions((prev) => {
			const next = new Set(prev);
			if (next.has(tier)) next.delete(tier);
			else next.add(tier);
			return next;
		});
	};

	useEffect(() => {
		if (catalogError) {
			setError(
				"Could not load pricing. Please refresh the page to try again.",
			);
		}
	}, [catalogError]);

	useEffect(() => {
		if (isAuthenticated) {
			refreshSubscription().catch((err) => {
				console.error("Failed to refresh subscription:", err);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated]);

	const formatPrice = (tierId: TierId): string => {
		if (tierId === "starter") return "Free";
		const plan = planFor(tierId);
		if (!plan || plan.amountCents === 0) return "—";
		return formatTierPrice(plan);
	};

	const handleSelectPlan = async (tierId: TierId) => {
		if (tierId === "starter") {
			navigate("/dashboard");
			return;
		}

		const plan = planFor(tierId);
		if (!plan?.stripePriceId) {
			setError(
				`The ${tierId} plan is not configured yet. Please contact support.`,
			);
			return;
		}

		try {
			setLoading(true);
			setLoadingTier(tierId);
			setError("");

			let token: string | undefined;
			if (isAuthenticated) {
				try {
					token = await getAccessTokenSilently({
						authorizationParams: {
							audience: "https://api.arteh.com",
							scope: "openid profile email",
						},
					});
				} catch (authError) {
					console.error("Could not get auth token:", authError);
				}
			} else {
				setError("Please login first to upgrade.");
				return;
			}

			let customerId = subscriptionState.stripeCustomerId;
			if (!customerId) {
				const refreshed = await refreshSubscription();
				customerId = refreshed?.stripeCustomerId || null;
				if (!customerId) {
					setError(
						"Please complete your profile setup first. Try clicking the button again or refresh the page.",
					);
					return;
				}
			}

			const { sessionId } = await stripeService.createCheckoutSession(
				plan.stripePriceId,
				customerId || undefined,
				token,
			);

			const stripe = await stripeService.getStripe();
			if (stripe && sessionId) {
				const { error: redirectError } =
					await stripe.redirectToCheckout({
						sessionId,
					});
				if (redirectError) {
					setError(
						redirectError.message ||
							"Failed to redirect to checkout",
					);
				}
			} else {
				setError(
					"Failed to initialize checkout session - check console for details.",
				);
			}
		} catch (err: any) {
			console.error("Checkout error:", err);
			setError(
				err.message ||
					"Failed to create checkout session. Please try again.",
			);
		} finally {
			setLoading(false);
			setLoadingTier(null);
		}
	};

	// Two-tier structure (tracker item 8): defined centrally in tierDisplay
	const tierIds: TierId[] = PRICING_PAGE_TIERS;

	return (
		<PageContainer>
			<StyledAppBar position="sticky">
				<Toolbar>
					<Box sx={{ flexGrow: 1 }} />
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate("/dashboard")}
						sx={{
							color: "#5DBEC3",
							textTransform: "none",
							fontWeight: 500,
						}}
					>
						Back to Dashboard
					</Button>
				</Toolbar>
			</StyledAppBar>

			<HeroSection>
				<Container maxWidth="lg">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
					>
						<Typography
							variant="h2"
							sx={{
								color: "#2c3e50",
								fontWeight: 600,
								fontSize: { xs: "2rem", md: "2.5rem" },
								marginBottom: "16px",
								lineHeight: 1.2,
							}}
						>
							Choose Your Sustainability Plan
						</Typography>
						<Typography
							variant="h6"
							sx={{
								color: "#6c757d",
								fontSize: "16px",
								fontWeight: 400,
								maxWidth: "640px",
								margin: "0 auto 32px auto",
								lineHeight: 1.5,
							}}
						>
							Track, analyze, and reduce your carbon footprint
							with the right level of support for your business.
						</Typography>

						{error && (
							<Alert
								severity="error"
								sx={{
									maxWidth: "600px",
									margin: "24px auto 0 auto",
									borderRadius: "8px",
								}}
								onClose={() => setError("")}
							>
								{error}
							</Alert>
						)}
					</motion.div>
				</Container>
			</HeroSection>

			<Container maxWidth="lg" sx={{ pb: 8 }}>
				{catalogLoading ? (
					<Box textAlign="center" py={6}>
						<CircularProgress />
					</Box>
				) : (
					<Grid
						container
						spacing={4}
						sx={{ justifyContent: "center" }}
					>
						{tierIds.map((tierId, index) => {
							const display = TIER_DISPLAY[tierId];
							return (
								<Grid
									item
									xs={12}
									sm={6}
									md={4}
									key={tierId}
									sx={{ display: "flex" }}
								>
									<motion.div
										initial={{ opacity: 0, y: 50 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{
											duration: 0.6,
											delay: index * 0.1,
										}}
										style={{
											width: "100%",
											height: "100%",
										}}
									>
										<PricingCard
											featured={display.featured}
										>
											{display.featured && (
												<FeaturedBadge label="Most Popular" />
											)}

											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													height: "100%",
												}}
											>
												<PlanIcon variant={tierId}>
													{display.icon}
												</PlanIcon>

												<Typography
													variant="h4"
													sx={{
														color: "#2c3e50",
														fontWeight: 600,
														textAlign: "center",
														mb: 1,
													}}
												>
													{display.name}
												</Typography>

												<Typography
													variant="body2"
													sx={{
														color: "#6c757d",
														textAlign: "center",
														mb: 3,
														minHeight: "60px",
													}}
												>
													{display.description}
												</Typography>

												<Box textAlign="center" mb={3}>
													<Typography
														variant="h3"
														sx={{
															color: "#2c3e50",
															fontWeight: 700,
															display: "inline",
														}}
													>
														{formatPrice(tierId)}
													</Typography>
													{tierId !== "starter" && (
														<Typography
															variant="body1"
															sx={{
																color: "#6c757d",
																display:
																	"inline",
																ml: 1,
															}}
														>
															/ year
														</Typography>
													)}
													{tierId !== "starter" && (
														<Typography
															variant="caption"
															sx={{
																display:
																	"block",
																color: "#6c757d",
																mt: 0.5,
																fontSize:
																	"12px",
															}}
														>
															GST included
														</Typography>
													)}
												</Box>

												<List
													dense
													sx={{ mb: 3, flexGrow: 1 }}
												>
													{display.included.map(
														(feature, idx) => (
															<FeatureItem
																key={`incl-${idx}`}
															>
																<ListItemIcon>
																	<CheckIcon
																		sx={{
																			color: "#5DBEC3",
																			fontSize:
																				"20px",
																		}}
																	/>
																</ListItemIcon>
																<ListItemText
																	primary={
																		<Typography
																			sx={{
																				color: "#2c3e50",
																				fontSize:
																					"14px",
																			}}
																		>
																			{
																				feature
																			}
																		</Typography>
																	}
																/>
															</FeatureItem>
														),
													)}
												</List>

												{display.excluded.length >
													0 && (
													<Box sx={{ mb: 2 }}>
														<Button
															size="small"
															onClick={() =>
																toggleExclusions(
																	tierId,
																)
															}
															endIcon={
																<ExpandMoreIcon
																	sx={{
																		transform:
																			expandedExclusions.has(
																				tierId,
																			)
																				? "rotate(180deg)"
																				: "none",
																		transition:
																			"transform 0.2s",
																	}}
																/>
															}
															sx={{
																color: "#6c757d",
																textTransform:
																	"none",
																fontSize:
																	"13px",
																padding: 0,
																"&:hover": {
																	background:
																		"none",
																	color: "#5DBEC3",
																},
															}}
														>
															{expandedExclusions.has(
																tierId,
															)
																? "Hide what's not included"
																: "What's not included"}
														</Button>
														<Collapse
															in={expandedExclusions.has(
																tierId,
															)}
														>
															<List
																dense
																sx={{ mt: 1 }}
															>
																{display.excluded.map(
																	(
																		feature,
																		idx,
																	) => (
																		<FeatureItem
																			key={`excl-${idx}`}
																		>
																			<ListItemIcon>
																				<CloseIcon
																					sx={{
																						color: "#dee2e6",
																						fontSize:
																							"20px",
																					}}
																				/>
																			</ListItemIcon>
																			<ListItemText
																				primary={
																					<Typography
																						sx={{
																							color: "#6c757d",
																							fontSize:
																								"14px",
																						}}
																					>
																						{
																							feature
																						}
																					</Typography>
																				}
																			/>
																		</FeatureItem>
																	),
																)}
															</List>
														</Collapse>
													</Box>
												)}

												<CTAButton
													buttonvariant={tierId}
													onClick={() =>
														handleSelectPlan(tierId)
													}
													disabled={
														loading ||
														(tierId !== "starter" &&
															!planFor(tierId)
																?.stripePriceId)
													}
												>
													{loading &&
													loadingTier === tierId ? (
														<CircularProgress
															size={24}
															sx={{
																color: "#fff",
															}}
														/>
													) : tierId !== "starter" &&
													  !planFor(tierId)
															?.stripePriceId ? (
														"Coming soon"
													) : (
														display.buttonText
													)}
												</CTAButton>
											</Box>
										</PricingCard>
									</motion.div>
								</Grid>
							);
						})}
					</Grid>
				)}

				<Box
					sx={{
						mt: 5,
						p: 3,
						textAlign: "center",
						borderTop: "1px solid #e0e0e0",
						borderBottom: "1px solid #e0e0e0",
					}}
				>
					<Typography
						variant="h6"
						sx={{
							color: "#2c3e50",
							fontWeight: 500,
							mb: 1,
						}}
					>
						Need invoice billing or purchase orders?
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "#6c757d",
							mb: 2,
							maxWidth: "600px",
							mx: "auto",
						}}
					>
						We support manual invoicing, POs, and flexible payment
						terms for enterprise customers.
					</Typography>
					<Button
						variant="outlined"
						size="medium"
						onClick={() => setContactSalesOpen(true)}
						sx={{
							borderColor: "#5DBEC3",
							color: "#5DBEC3",
							textTransform: "none",
							px: 4,
							"&:hover": {
								borderColor: "#4BA3A7",
								backgroundColor: "rgba(93, 190, 195, 0.04)",
							},
						}}
					>
						Contact Sales →
					</Button>
				</Box>

				<Box textAlign="center" mt={6}>
					<Typography
						variant="body2"
						sx={{ color: "#6c757d", mb: 2 }}
					>
						No setup fees • Cancel anytime • Secure payment with
						Stripe
					</Typography>
				</Box>
			</Container>

			<ContactSalesModal
				open={contactSalesOpen}
				onClose={() => setContactSalesOpen(false)}
				context="From /pricing — Enterprise billing & PO inquiry"
			/>
		</PageContainer>
	);
};

export default ModernPricing;
